// src/components/data-display/BitcoinCalculatorAdvanced.jsx


import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  Select,
  FormControl,
  FormLabel,
  useColorMode,
  Text,
  Button,
  ButtonGroup,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Box,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Switch,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBitcoinSign, faWallet, faCalculator, faBullseye, faTimes, faPencilAlt, faBookmark, faTrash, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import { useRecoilState } from 'recoil';
import { selectedCurrencyState } from '../../recoilState';
import { createCurrencyConverter, currencySymbols } from '../../utils/currencyConversion.utils';
import { buildTradePreview, calcNetGainPct } from '../../utils/tradeCalculations';

const formatFiatAmount = (amount) => {
  return isNaN(amount) ? '0' : Math.round(amount).toLocaleString();
};

const formatSatsAmount = (amount) => {
  return isNaN(amount) ? '0' : parseInt(amount).toLocaleString();
};

const convertCryptoToAmount = (cryptoAmount, bitcoinPrice, conversionRate) => {
  const sanitizedValue = parseFloat(cryptoAmount.replace(/,/g, ''));
  if (!isNaN(sanitizedValue) && !isNaN(bitcoinPrice) && !isNaN(conversionRate)) {
    const convertedAmount = Math.round(sanitizedValue * bitcoinPrice * conversionRate);
    return formatFiatAmount(convertedAmount);
  } else {
    return '0';
  }
};

const formatLargeNumber = (num) => {
  if (typeof num !== 'number' || isNaN(num)) {
    return '0.00';
  }
  const absNum = Math.abs(num);
  if (absNum >= 1e15) return (num / 1e15).toFixed(2) + 'Qu';
  if (absNum >= 1e12) return (num / 1e12).toFixed(2) + 'T';
  if (absNum >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (absNum >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (absNum >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toFixed(2);
};

const PAPER_TRADES_KEY = 'timechainstats_paper_trades';
const TRADE_SETTINGS_KEY = 'timechainstats_trade_settings';
const TRADE_DEFAULTS = {
  tradeAction: 'BUY', tradePrice: '', tradeBtcAmount: '',
  tradeTargetPct: '2', targetMode: 'gain', tradeStopLossPct: '2',
  tradeFeeRate: '0.5', taxEnabled: false, taxType: 'long', taxRate: 15,
};
const loadTradeSettings = () => {
  try { return { ...TRADE_DEFAULTS, ...JSON.parse(localStorage.getItem(TRADE_SETTINGS_KEY)) }; }
  catch { return TRADE_DEFAULTS; }
};

const formatBtcValue = (value) => {
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return '0';
  return num.toFixed(8);
};

export const BitcoinCalculatorAdvanced = ({ isOpen, onClose, data, bitcoinPrice, formatNumber, defaultMode = 'btc' }) => {
  const [calculatorMode, setCalculatorMode] = useState(defaultMode);
  const gapValue = useBreakpointValue({ base: 4, md: 4 });
  const [cryptoAmount, setCryptoAmount] = useState('1');
  const [satsAmount, setSatsAmount] = useState('100,000,000');
  const [cryptoCurrency, setCryptoCurrency] = useState('BTC');
  const [fiatAmount, setFiatAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useRecoilState(selectedCurrencyState);
  const [conversionRates, setConversionRates] = useState({});
  const [portfolio, setPortfolio] = useState([]);
  const [newEntry, setNewEntry] = useState({
    btcAmount: '',
    purchasePrice: '',
    purchaseDate: '',
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const prevCurrencyRef = useRef(selectedCurrency);

  // Trade calculator state (persisted to localStorage)
  const savedTradeSettings = useRef(loadTradeSettings());
  const [tradeAction, setTradeAction] = useState(savedTradeSettings.current.tradeAction);
  const [tradePrice, setTradePrice] = useState(savedTradeSettings.current.tradePrice);
  const [tradeBtcAmount, setTradeBtcAmount] = useState(savedTradeSettings.current.tradeBtcAmount);
  const [tradeTargetPct, setTradeTargetPct] = useState(savedTradeSettings.current.tradeTargetPct);
  const [targetMode, setTargetMode] = useState(savedTradeSettings.current.targetMode);
  const [tradeStopLossPct, setTradeStopLossPct] = useState(savedTradeSettings.current.tradeStopLossPct);
  const [tradeFeeRate, setTradeFeeRate] = useState(savedTradeSettings.current.tradeFeeRate);
  const [taxEnabled, setTaxEnabled] = useState(savedTradeSettings.current.taxEnabled);
  const [taxType, setTaxType] = useState(savedTradeSettings.current.taxType);
  const [taxRate, setTaxRate] = useState(savedTradeSettings.current.taxRate);

  // Paper trades state
  const [paperTrades, setPaperTrades] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(PAPER_TRADES_KEY)) || [];
    } catch { return []; }
  });

  // Persist trade settings to localStorage
  useEffect(() => {
    localStorage.setItem(TRADE_SETTINGS_KEY, JSON.stringify({
      tradeAction, tradePrice, tradeBtcAmount, tradeTargetPct,
      targetMode, tradeStopLossPct, tradeFeeRate, taxEnabled, taxType, taxRate,
    }));
  }, [tradeAction, tradePrice, tradeBtcAmount, tradeTargetPct, targetMode, tradeStopLossPct, tradeFeeRate, taxEnabled, taxType, taxRate]);

  const { colorMode } = useColorMode();
  const bgColor = colorMode === 'dark' ? '#121212' : '#ffffff';
  const borderColor = colorMode === 'dark' ? '#ffffff' : '#121212';

  const isMobile = useBreakpointValue({ base: true, md: false });

  const [currencyChanged, setCurrencyChanged] = useState(false);

const shouldShowWarning = useCallback(() => {
  return currencyChanged && portfolio.length > 0;
}, [currencyChanged, portfolio.length]);

useEffect(() => {
  if (data) {
    const newConversionRates = createCurrencyConverter(data);
    if (newConversionRates) {
      setConversionRates(newConversionRates);
    }
  }
  
  // Check if the currency has changed
  if (prevCurrencyRef.current !== selectedCurrency) {
    console.log('Currency changed from', prevCurrencyRef.current, 'to', selectedCurrency);
    prevCurrencyRef.current = selectedCurrency;
    setCurrencyChanged(true);
  }
}, [data, selectedCurrency]);

useEffect(() => {
  setCurrencyChanged(false);
}, [portfolio]);

  useEffect(() => {
    if (bitcoinPrice && conversionRates[selectedCurrency]) {
      setFiatAmount(convertCryptoToAmount(cryptoAmount, bitcoinPrice, conversionRates[selectedCurrency]));
    }
  }, [bitcoinPrice, selectedCurrency, cryptoAmount, conversionRates]);

  const handleCryptoAmountChange = useCallback((e) => {
    const value = e.target.value;
    setCryptoAmount(value);

    const satsValue = Math.floor(parseFloat(value) * 100000000);
    setSatsAmount(formatSatsAmount(satsValue));
    if (bitcoinPrice && conversionRates[selectedCurrency]) {
      setFiatAmount(convertCryptoToAmount(value, bitcoinPrice, conversionRates[selectedCurrency]));
    }
  }, [bitcoinPrice, conversionRates, selectedCurrency]);

  const handleSatsAmountChange = useCallback((e) => {
    const value = e.target.value.replace(/,/g, '');
    const numericValue = parseInt(value, 10);

    if (!isNaN(numericValue)) {
      setSatsAmount(formatSatsAmount(numericValue));

      const btcValue = (numericValue / 100000000).toFixed(8);
      setCryptoAmount(btcValue);
      if (bitcoinPrice && conversionRates[selectedCurrency]) {
        setFiatAmount(convertCryptoToAmount(btcValue, bitcoinPrice, conversionRates[selectedCurrency]));
      }
    } else {
      setSatsAmount('0');
      setCryptoAmount('0');
      setFiatAmount('0');
    }
  }, [bitcoinPrice, conversionRates, selectedCurrency]);

  const handleFiatAmountChange = useCallback((e) => {
    const value = e.target.value.replace(/,/g, '');
    const numericValue = parseFloat(value);

    if (!isNaN(numericValue) && bitcoinPrice && conversionRates[selectedCurrency]) {
      const formattedValue = numericValue.toLocaleString();
      setFiatAmount(formattedValue);

      const convertedBTCAmount = (numericValue / (bitcoinPrice * conversionRates[selectedCurrency])).toFixed(8);
      setCryptoAmount(convertedBTCAmount);

      const satsValue = Math.floor(parseFloat(convertedBTCAmount) * 100000000);
      setSatsAmount(formatSatsAmount(satsValue));
    } else {
      setFiatAmount('0');
      setCryptoAmount('0');
      setSatsAmount('0');
    }
  }, [bitcoinPrice, conversionRates, selectedCurrency]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewEntry(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleEditClick = useCallback((entry) => {
    setEditingEntry(entry);
    setNewEntry({
      btcAmount: entry.btcAmount,
      purchasePrice: entry.purchasePrice,
      purchaseDate: entry.purchaseDate,
    });
  }, []);

  const clearFields = useCallback(() => {
    setNewEntry({ btcAmount: '', purchasePrice: '', purchaseDate: '' });
  }, []);

  const addOrUpdateEntry = useCallback(() => {
    if (newEntry.btcAmount && newEntry.purchasePrice && newEntry.purchaseDate) {
      const entry = {
        ...newEntry,
        btcAmount: parseFloat(newEntry.btcAmount) || 0,
        purchasePrice: parseFloat(newEntry.purchasePrice) || 0,
        id: editingEntry ? editingEntry.id : Date.now()
      };

      setPortfolio(prevPortfolio => {
        if (editingEntry) {
          return prevPortfolio.map(item => item.id === editingEntry.id ? entry : item);
        } else {
          return [...prevPortfolio, entry];
        }
      });

      setEditingEntry(null);
      clearFields();
    }
  }, [newEntry, editingEntry, clearFields]);

  const cancelUpdate = useCallback(() => {
    setEditingEntry(null);
    clearFields();
  }, [clearFields]);

  const removeEntry = useCallback((id) => {
    setPortfolio(prevPortfolio => prevPortfolio.filter(entry => entry.id !== id));
  }, []);

  const calculateTotals = useCallback(() => {
    return portfolio.reduce((acc, entry) => {
      const currentValue = entry.btcAmount * (bitcoinPrice || 0) * conversionRates[selectedCurrency];
      const originalValue = entry.btcAmount * entry.purchasePrice;
      const profit = currentValue - originalValue;

      return {
        totalBtc: acc.totalBtc + (parseFloat(entry.btcAmount) || 0),
        totalValue: acc.totalValue + currentValue,
        totalCostBasis: acc.totalCostBasis + originalValue,
        totalProfit: acc.totalProfit + profit,
        totalOriginalOrderPrice: acc.totalOriginalOrderPrice + originalValue,
      };
    }, { totalBtc: 0, totalValue: 0, totalCostBasis: 0, totalProfit: 0, totalOriginalOrderPrice: 0 });
  }, [portfolio, bitcoinPrice, conversionRates, selectedCurrency]);

  const formatCurrency = useCallback((value) => {
    if (isNaN(value)) return '0.00';
    const formattedValue = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: selectedCurrency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return formatLargeNumber(parseFloat(formattedValue.replace(/[^0-9.-]+/g, '')));
  }, [selectedCurrency]);

  const calculateDaysHeld = useCallback((purchaseDate) => {
    const today = new Date();
    const purchase = new Date(purchaseDate);
    const diffTime = Math.abs(today - purchase);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  const calculateProfitLossPercentage = (currentValue, originalValue) => {
    if (originalValue === 0) return 0;
    return ((currentValue - originalValue) / originalValue) * 100;
  };

  // Auto-fill trade price with live price in selected currency
  const livePriceInCurrency = bitcoinPrice && conversionRates[selectedCurrency]
    ? Math.round(bitcoinPrice * conversionRates[selectedCurrency] * 100) / 100
    : 0;

  const parsedTradePrice = parseFloat(tradePrice) || livePriceInCurrency;
  const parsedTradeBtc = parseFloat(tradeBtcAmount) || 0;
  const parsedTargetPct = parseFloat(tradeTargetPct) || 0;
  const parsedStopLossPct = parseFloat(tradeStopLossPct) || 0;
  const parsedFeeRate = (parseFloat(tradeFeeRate) || 0) / 100;

  // In 'move' mode, convert price move % to net gain % for the preview
  const effectiveTargetPct = useMemo(() => {
    if (targetMode === 'gain' || parsedTargetPct === 0) return parsedTargetPct;
    const exitPrice = tradeAction === 'BUY'
      ? parsedTradePrice * (1 + parsedTargetPct / 100)
      : parsedTradePrice * (1 - parsedTargetPct / 100);
    return calcNetGainPct(tradeAction, parsedTradePrice, exitPrice, parsedFeeRate);
  }, [targetMode, tradeAction, parsedTradePrice, parsedTargetPct, parsedFeeRate]);

  const tradePreview = useMemo(() => {
    if (parsedTradePrice <= 0 || parsedTradeBtc <= 0) return null;
    return buildTradePreview(tradeAction, parsedTradePrice, parsedTradeBtc, effectiveTargetPct, parsedStopLossPct, parsedFeeRate);
  }, [tradeAction, parsedTradePrice, parsedTradeBtc, effectiveTargetPct, parsedStopLossPct, parsedFeeRate]);

  const formatTradeAmount = useCallback((value) => {
    if (isNaN(value) || value === null) return '--';
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Paper trades persistence
  useEffect(() => {
    localStorage.setItem(PAPER_TRADES_KEY, JSON.stringify(paperTrades));
  }, [paperTrades]);

  const savePaperTrade = useCallback(() => {
    if (!tradePreview) return;
    setPaperTrades(prev => [{
      id: Date.now(),
      timestamp: new Date().toISOString(),
      action: tradeAction,
      entryPrice: parsedTradePrice,
      btcAmount: parsedTradeBtc,
      targetPct: parsedTargetPct,
      targetMode,
      stopLossPct: parsedStopLossPct,
      feeRate: parsedFeeRate,
      taxEnabled,
      taxType,
      taxRate,
      currency: selectedCurrency,
      targetPrice: tradePreview.targetPrice,
      breakevenPrice: tradePreview.breakevenPrice,
      stopLossPrice: tradePreview.stopLossPrice,
      gainAtTarget: tradePreview.gainAtTarget,
    }, ...prev]);
  }, [tradePreview, tradeAction, parsedTradePrice, parsedTradeBtc, parsedTargetPct, targetMode, parsedStopLossPct, parsedFeeRate, taxEnabled, taxType, taxRate, selectedCurrency]);

  const deletePaperTrade = useCallback((id) => {
    setPaperTrades(prev => prev.filter(t => t.id !== id));
  }, []);

  const exportPositionsCSV = useCallback(() => {
    if (!paperTrades.length) return;
    const headers = [
      'Saved At', 'Action', 'Currency', 'Entry Price', 'BTC Amount',
      'Target %', 'Target Mode', 'Target Price', 'Stop Loss %', 'Stop Loss Price',
      'Fee Rate %', 'Breakeven Price', 'Projected Gain at Target',
      'Tax Enabled', 'Tax Type', 'Tax Rate %',
    ];
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const rows = paperTrades.map((t) => [
      t.timestamp,
      t.action,
      (t.currency || '').toUpperCase(),
      t.entryPrice,
      t.btcAmount,
      t.targetPct,
      t.targetMode,
      t.targetPrice,
      t.stopLossPct,
      t.stopLossPrice,
      (t.feeRate * 100).toFixed(2),
      t.breakevenPrice,
      t.gainAtTarget,
      t.taxEnabled ? 'yes' : 'no',
      t.taxEnabled ? t.taxType : '',
      t.taxEnabled ? t.taxRate : '',
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStr = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `timechainstats-positions-${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [paperTrades]);

  const loadPaperTrade = useCallback((trade) => {
    setTradeAction(trade.action);
    setTradePrice(String(trade.entryPrice));
    setTradeBtcAmount(String(trade.btcAmount));
    setTradeTargetPct(String(trade.targetPct));
    setTargetMode(trade.targetMode || 'gain');
    setTradeStopLossPct(String(trade.stopLossPct));
    setTradeFeeRate(String((trade.feeRate * 100).toFixed(2)));
    setTaxEnabled(trade.taxEnabled || false);
    setTaxType(trade.taxType || 'long');
    setTaxRate(trade.taxRate || 15);
  }, []);

  const renderMobileEntry = useCallback((entry) => {
    const currentValue = entry.btcAmount * (bitcoinPrice || 0) * conversionRates[selectedCurrency];
    const originalValue = entry.btcAmount * entry.purchasePrice;
    const profit = currentValue - originalValue;
    const profitPercentage = calculateProfitLossPercentage(currentValue, originalValue);
    const daysHeld = calculateDaysHeld(entry.purchaseDate);

    return (
      <Box 
        key={entry.id} 
        p={{ base: 3, sm: 4 }} 
        borderWidth="1px" 
        borderRadius="lg" 
        mb={3}
        bg={colorMode === 'dark' ? 'gray.800' : 'gray.50'}
      >
        <VStack align="stretch" spacing={2}>
          <Flex justifyContent="space-between" alignItems="flex-start">
            <VStack align="flex-start" spacing={1} flex="1">
              <Text fontSize="sm" fontWeight="bold" color="gray.500">BTC Amount</Text>
              <Text fontSize="md">{formatLargeNumber(entry.btcAmount)}</Text>
            </VStack>
            <HStack spacing={2}>
              <IconButton
                icon={<FontAwesomeIcon icon={faPencilAlt} />}
                size="xs"
                variant="ghost"
                colorScheme='orange'
                onClick={() => handleEditClick(entry)}
                aria-label="Edit entry"
              />
              <IconButton
                icon={<FontAwesomeIcon icon={faTimes} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => removeEntry(entry.id)}
                aria-label="Remove entry"
              />
            </HStack>
          </Flex>
          
          <Flex justifyContent="space-between" flexWrap="wrap" gap={2}>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Purchase Price</Text>
              <Text fontSize="sm">{formatCurrency(entry.purchasePrice)}</Text>
            </VStack>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Purchase Date</Text>
              <Text fontSize="sm">{entry.purchaseDate}</Text>
            </VStack>
          </Flex>
          
          <Flex justifyContent="space-between" flexWrap="wrap" gap={2}>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Cost Basis</Text>
              <Text fontSize="sm">{formatCurrency(originalValue)}</Text>
            </VStack>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Current Value</Text>
              <Text fontSize="sm">{formatCurrency(currentValue)}</Text>
            </VStack>
          </Flex>
          
          <Box 
            p={2} 
            borderRadius="md" 
            bg={profit >= 0 ? 'green.50' : 'red.50'}
            _dark={{ bg: profit >= 0 ? 'green.900' : 'red.900' }}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="bold" color={profit >= 0 ? 'green.600' : 'red.600'} _dark={{ color: profit >= 0 ? 'green.200' : 'red.200' }}>
                P/L: {formatCurrency(profit)}
              </Text>
              <HStack spacing={2}>
                <Text fontSize="sm" color={profit >= 0 ? 'green.600' : 'red.600'} _dark={{ color: profit >= 0 ? 'green.200' : 'red.200' }}>
                  {formatNumber(profitPercentage, 2)}%
                </Text>
                <Text fontSize="xs" color="gray.500">
                  ({daysHeld}d)
                </Text>
              </HStack>
            </Flex>
          </Box>
        </VStack>
      </Box>
    );
  }, [bitcoinPrice, formatCurrency, handleEditClick, removeEntry, calculateDaysHeld, formatNumber, conversionRates, selectedCurrency, colorMode]);

  const totals = calculateTotals();

  if (!bitcoinPrice) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
        <ModalContent bg={bgColor} pb={6} borderWidth="2px" borderStyle="solid" borderColor={borderColor} borderRadius="6px" overflow="hidden">
          <ModalHeader>Loading...</ModalHeader>
          <ModalBody>
            <Text>Waiting for the live Bitcoin price...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }



  return (
    <Modal isOpen={isOpen} onClose={onClose} size={calculatorMode === 'pnl' ? "6xl" : calculatorMode === 'trade' ? "3xl" : "xl"}>
      <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
      <ModalContent bg={bgColor} pb={6} borderWidth="2px" borderStyle="solid" borderColor={borderColor} borderRadius="6px" overflow="hidden">
        <ModalHeader>
          <Flex align="center" justify="flex-start" wrap="wrap" gap={2}>
            <FontAwesomeIcon icon={calculatorMode === 'btc' ? faCalculator : calculatorMode === 'pnl' ? faWallet : faBullseye} style={{ marginRight: '6px' }} />
            <Text mr={2} fontSize={{ base: 'md', md: 'lg' }}>
              {calculatorMode === 'btc' ? 'BTC Calculator' : calculatorMode === 'pnl' ? 'Profit/Loss Calculator' : 'Paper Trader'}
            </Text>
            <ButtonGroup size="xs" isAttached>
              <Button
                colorScheme={calculatorMode === 'btc' ? 'orange' : 'gray'}
                variant={calculatorMode === 'btc' ? 'solid' : 'outline'}
                onClick={() => setCalculatorMode('btc')}
              >
                BTC
              </Button>
              <Button
                colorScheme={calculatorMode === 'pnl' ? 'orange' : 'gray'}
                variant={calculatorMode === 'pnl' ? 'solid' : 'outline'}
                onClick={() => setCalculatorMode('pnl')}
              >
                P/L
              </Button>
              <Button
                colorScheme={calculatorMode === 'trade' ? 'orange' : 'gray'}
                variant={calculatorMode === 'trade' ? 'solid' : 'outline'}
                onClick={() => setCalculatorMode('trade')}
              >
                Trade
              </Button>
            </ButtonGroup>
          </Flex>
        </ModalHeader>
        <ModalCloseButton
          _hover={{
            bg: 'gray.500',
          }}
          _focus={{
            boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
          }}
        />
        <ModalBody>
          {calculatorMode === 'btc' ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={gapValue} width="100%">
              <FormControl>
                <FormLabel>Bitcoin</FormLabel>
                <InputGroup>
                  <InputLeftAddon><FontAwesomeIcon icon={faBitcoinSign} /></InputLeftAddon>
                  <Input type="number" value={cryptoAmount} onChange={handleCryptoAmountChange} borderWidth="1.5px" fontSize={'16px'} />
                  <InputRightElement width="auto">
                    <Select value={cryptoCurrency} onChange={(e) => setCryptoCurrency(e.target.value)} icon={'none'}>
                      <option value="BTC">BTC</option>
                    </Select>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Sats</FormLabel>
                <InputGroup>
                  <InputLeftAddon fontSize={16}>ṩ</InputLeftAddon>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{1,3}(,\d{3})*"
                    value={satsAmount}
                    onChange={handleSatsAmountChange}
                    borderWidth="1.5px"
                    fontSize={'16px'}
                  />
                  <InputRightElement width="auto">
                    <Select value={cryptoCurrency} onChange={(e) => setCryptoCurrency(e.target.value)} icon={'none'}>
                      <option value="Sats">Sats</option>
                    </Select>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl gridColumn={{ md: '1 / -1' }}>
                <FormLabel>Conversion</FormLabel>
                <InputGroup>
                  <InputLeftAddon>{currencySymbols[selectedCurrency] || '$'}</InputLeftAddon>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{1,3}(,\d{3})*"
                    value={fiatAmount}
                    onChange={handleFiatAmountChange}
                    borderWidth="1.5px"
                    fontSize={'16px'}
                  />
                  <InputRightElement width="auto" borderWidth="2px" borderRadius="6px" >
                    <Select value={selectedCurrency} onChange={(e) => setSelectedCurrency(e.target.value)}>
                      {Object.keys(currencySymbols).map((currency) => (
                        <option key={currency} value={currency}>{currency.toUpperCase()}</option>
                      ))}
                    </Select>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            </SimpleGrid>
          ) : calculatorMode === 'pnl' ? (
            <>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={{ base: 3, md: 4 }}
                mb={4}
                flexWrap={{ md: 'wrap', lg: 'nowrap' }}
              >
                <FormControl flex={{ base: '1', md: '1', lg: '1' }} minW={{ base: '100%', md: '45%', lg: 'auto' }}>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }}>BTC Amount</FormLabel>
                  <InputGroup size={{ base: 'md', md: 'md' }}>
                    <InputLeftAddon><FontAwesomeIcon icon={faBitcoinSign} /></InputLeftAddon>
                    <Input
                      type="number"
                      name="btcAmount"
                      value={newEntry.btcAmount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      fontSize={{ base: '16px', md: '16px' }}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl flex={{ base: '1', md: '1', lg: '1' }} minW={{ base: '100%', md: '45%', lg: 'auto' }}>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Purchase Price ({selectedCurrency.toUpperCase()})</FormLabel>
                  <InputGroup size={{ base: 'md', md: 'md' }}>
                    <InputLeftAddon>{currencySymbols[selectedCurrency] || '$'}</InputLeftAddon>
                    <Input
                      type="number"
                      name="purchasePrice"
                      value={newEntry.purchasePrice}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      fontSize={{ base: '16px', md: '16px' }}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl flex={{ base: '1', md: '1', lg: '1' }} minW={{ base: '100%', md: '45%', lg: 'auto' }}>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Purchase Date</FormLabel>
                  <Input
                    type="date"
                    name="purchaseDate"
                    value={newEntry.purchaseDate}
                    onChange={handleInputChange}
                    size={{ base: 'md', md: 'md' }}
                    fontSize={{ base: '16px', md: '16px' }}
                  />
                </FormControl>
              </Flex>
              <Flex 
                justifyContent="center" 
                width="100%" 
                gap={{ base: 2, md: 4 }}
                flexWrap="wrap"
                mb={6}
              >
                <Button
                  onClick={addOrUpdateEntry}
                  size={{ base: "sm", md: "md" }}
                  colorScheme={editingEntry ? "green" : "orange"}
                  minW={{ base: "30%", md: "auto" }}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  {editingEntry ? 'Update Entry' : 'Add Entry'}
                </Button>
  
                {editingEntry && (
                  <Button
                    onClick={cancelUpdate}
                    size={{ base: "sm", md: "md" }}
                    colorScheme="red"
                    minW={{ base: "30%", md: "auto" }}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    Cancel Update
                  </Button>
                )}
  
                <Button
                  onClick={clearFields}
                  size={{ base: "sm", md: "md" }}
                  colorScheme="gray"
                  minW={{ base: "20%", md: "auto" }}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  Clear
                </Button>
              </Flex>
  
              {isMobile ? (
                <VStack mt={6} spacing={4} align="stretch">
                  {portfolio.map(renderMobileEntry)}
                </VStack>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" mt={6} size="sm">
                    <Thead>
                      <Tr>
                        <Th>BTC Amount</Th>
                        <Th>Purchase Price</Th>
                        <Th>Purchase Date</Th>
                        <Th>Original Order Total</Th>
                        <Th>Current Value</Th>
                        <Th>Profit/Loss</Th>
                        <Th>P/L %</Th>
                        <Th>Days Held</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {portfolio.map((entry) => {
                        const currentValue = entry.btcAmount * (bitcoinPrice || 0) * conversionRates[selectedCurrency];
                        const originalValue = entry.btcAmount * entry.purchasePrice;
                        const profit = currentValue - originalValue;
                        const profitPercentage = calculateProfitLossPercentage(currentValue, originalValue);
                        const daysHeld = calculateDaysHeld(entry.purchaseDate);
                        return (
                          <Tr key={entry.id}>
                            <Td>{formatLargeNumber(entry.btcAmount)}</Td>
                            <Td>{formatCurrency(entry.purchasePrice)}</Td>
                            <Td>{entry.purchaseDate}</Td>
                            <Td>{formatCurrency(originalValue)}</Td>
                            <Td>{formatCurrency(currentValue)}</Td>
                            <Td color={profit >= 0 ? 'green.500' : 'red.500'}>
                              {formatCurrency(profit)}
                            </Td>
                            <Td color={profit >= 0 ? 'green.500' : 'red.500'}>
                              {formatNumber(profitPercentage, 2)}%
                            </Td>
                            <Td>{formatNumber(daysHeld, 0)}</Td>
                            <Td>
                              <Flex>
                                <IconButton
                                  icon={<FontAwesomeIcon icon={faPencilAlt} />}
                                  size="xs"
                                  colorScheme="orange"
                                  mr={2}
                                  onClick={() => handleEditClick(entry)}
                                  aria-label="Edit entry"
                                />
                                <IconButton
                                  icon={<FontAwesomeIcon icon={faTimes} />}
                                  size="xs"
                                  colorScheme="red"
                                  onClick={() => removeEntry(entry.id)}
                                  aria-label="Remove entry"
                                />
                              </Flex>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
  
              <Flex 
                direction={{ base: 'column', lg: 'row' }}
                justifyContent={{ base: 'flex-start', lg: 'space-between' }}
                alignItems={{ base: 'stretch', lg: 'flex-end' }}
                mt={6} 
                gap={{ base: 4, lg: 6 }}
              >
                <Box flex="1">
                  <Flex 
                    direction={{ base: 'column', sm: 'row' }}
                    gap={{ base: 4, sm: 6 }}
                    flexWrap="wrap"
                  >
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Stack Size</Text>
                      <Text fontSize={{ base: 'md', md: 'lg' }}>{formatLargeNumber(totals.totalBtc)} BTC</Text>
                    </VStack>
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Cost Basis</Text>
                      <Text fontSize={{ base: 'md', md: 'lg' }}>{formatCurrency(totals.totalOriginalOrderPrice)}</Text>
                    </VStack>
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Current Value</Text>
                      <Text fontSize={{ base: 'md', md: 'lg' }}>{formatCurrency(totals.totalValue)}</Text>
                    </VStack>
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Profit/Loss</Text>
                      <Text 
                        color={totals.totalProfit >= 0 ? 'green.500' : 'red.500'}
                        fontSize={{ base: 'md', md: 'lg' }}
                      >
                        {formatCurrency(totals.totalProfit)} ({formatNumber(calculateProfitLossPercentage(totals.totalValue, totals.totalOriginalOrderPrice), 2)}%)
                      </Text>
                    </VStack>
                  </Flex>
                </Box>
                <VStack
                  align={{ base: 'flex-start', lg: 'flex-end' }}
                  spacing={1}
                  minW={{ base: '100%', lg: 'auto' }}
                  mt={{ base: 2, lg: 0 }}
                >
                  <Text
                    fontSize="xs"
                    color="gray.500"
                  >
                    For estimation or informational purposes only.
                  </Text>
                  {shouldShowWarning() && (
                    <Text
                      fontSize="xs"
                      color="red.500"
                      fontWeight="bold"
                    >
                      Currency changed. Some calculations may need to be updated.
                    </Text>
                  )}
                </VStack>
              </Flex>
            </>
          ) : (
            /* Paper Trader */
            <>
            <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
              {/* Left: Inputs */}
              <VStack spacing={4} align="stretch" flex={1}>
                {/* BUY / SELL toggle */}
                <ButtonGroup size="sm" isAttached w="full">
                  <Button
                    flex={1}
                    colorScheme={tradeAction === 'BUY' ? 'green' : 'gray'}
                    variant={tradeAction === 'BUY' ? 'solid' : 'outline'}
                    onClick={() => setTradeAction('BUY')}
                  >
                    BUY
                  </Button>
                  <Button
                    flex={1}
                    colorScheme={tradeAction === 'SELL' ? 'red' : 'gray'}
                    variant={tradeAction === 'SELL' ? 'solid' : 'outline'}
                    onClick={() => setTradeAction('SELL')}
                  >
                    SELL
                  </Button>
                </ButtonGroup>

                {/* Input fields */}
                <SimpleGrid columns={2} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? 'orange.700' : 'blue.500'} textTransform="uppercase">
                      BTC Price ({selectedCurrency.toUpperCase()})
                    </FormLabel>
                    <NumberInput
                      value={tradePrice || (livePriceInCurrency ? formatTradeAmount(livePriceInCurrency) : '')}
                      onChange={(val) => setTradePrice(val)}
                      min={0}
                      precision={2}
                    >
                      <NumberInputField fontSize="16px" />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? 'orange.700' : 'blue.500'} textTransform="uppercase">BTC Amount</FormLabel>
                    <NumberInput
                      value={tradeBtcAmount}
                      onChange={(val) => setTradeBtcAmount(val)}
                      min={0}
                      step={0.001}
                      precision={8}
                    >
                      <NumberInputField placeholder="0.00000000" fontSize="16px" />
                    </NumberInput>
                  </FormControl>
                  <FormControl gridColumn="1 / -1">
                    <Flex align="center" gap={2} mb={1}>
                      <FormLabel fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? 'orange.700' : 'blue.500'} textTransform="uppercase" mb={0}>
                        Target %
                      </FormLabel>
                      <ButtonGroup size="xs" isAttached>
                        <Button
                          variant={targetMode === 'gain' ? 'solid' : 'outline'}
                          colorScheme={targetMode === 'gain' ? 'orange' : 'gray'}
                          onClick={() => setTargetMode('gain')}
                        >
                          Net Gain
                        </Button>
                        <Button
                          variant={targetMode === 'move' ? 'solid' : 'outline'}
                          colorScheme={targetMode === 'move' ? 'orange' : 'gray'}
                          onClick={() => setTargetMode('move')}
                        >
                          Price Move
                        </Button>
                      </ButtonGroup>
                    </Flex>
                    <NumberInput
                      value={tradeTargetPct}
                      onChange={(val) => setTradeTargetPct(val)}
                      min={0}
                      step={0.5}
                      precision={2}
                      size="sm"
                    >
                      <NumberInputField placeholder="2.00" fontSize="16px" />
                    </NumberInput>
                  </FormControl>
                  <Flex gap={3} gridColumn="1 / -1">
                    <FormControl maxW="100px">
                      <FormLabel fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? 'orange.700' : 'blue.500'} textTransform="uppercase">Stop Loss %</FormLabel>
                      <NumberInput
                        value={tradeStopLossPct}
                        onChange={(val) => setTradeStopLossPct(val)}
                        min={0}
                        step={0.5}
                        precision={2}
                        size="sm"
                      >
                        <NumberInputField placeholder="2.00" fontSize="16px" />
                      </NumberInput>
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? 'orange.700' : 'blue.500'} textTransform="uppercase">Fee Rate %</FormLabel>
                      <HStack>
                        <NumberInput
                          value={tradeFeeRate}
                          onChange={(val) => setTradeFeeRate(val)}
                          min={0}
                          max={10}
                          step={0.1}
                          precision={2}
                          size="sm"
                          maxW="80px"
                        >
                          <NumberInputField fontSize="16px" />
                        </NumberInput>
                        <ButtonGroup size="xs" isAttached>
                          {[
                            { label: '0%', value: '0.00' },
                            { label: '0.5%', value: '0.50' },
                            { label: '1%', value: '1.00' },
                          ].map((preset) => (
                            <Button
                              key={preset.value}
                              variant={parseFloat(tradeFeeRate) === parseFloat(preset.value) ? 'solid' : 'outline'}
                              colorScheme={parseFloat(tradeFeeRate) === parseFloat(preset.value) ? 'orange' : 'gray'}
                              onClick={() => setTradeFeeRate(preset.value)}
                            >
                              {preset.label}
                            </Button>
                          ))}
                        </ButtonGroup>
                      </HStack>
                    </FormControl>
                  </Flex>
                </SimpleGrid>

                {/* Tax toggle */}
                <FormControl>
                  <Flex align="center" gap={2} mb={taxEnabled ? 2 : 0}>
                    <FormLabel fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? 'orange.700' : 'blue.500'} textTransform="uppercase" mb={0}>
                      Capital Gains Tax
                    </FormLabel>
                    <Switch size="sm" colorScheme="orange" isChecked={taxEnabled} onChange={(e) => setTaxEnabled(e.target.checked)} />
                  </Flex>
                  {taxEnabled && (
                    <Box>
                      <Flex align="center" gap={2}>
                        <ButtonGroup size="xs" isAttached>
                          <Button
                            variant={taxType === 'long' ? 'solid' : 'outline'}
                            colorScheme={taxType === 'long' ? 'orange' : 'gray'}
                            onClick={() => { setTaxType('long'); setTaxRate(15); }}
                          >
                            Long-term
                          </Button>
                          <Button
                            variant={taxType === 'short' ? 'solid' : 'outline'}
                            colorScheme={taxType === 'short' ? 'orange' : 'gray'}
                            onClick={() => { setTaxType('short'); setTaxRate(37); }}
                          >
                            Short-term
                          </Button>
                        </ButtonGroup>
                        <Text fontSize="sm" fontWeight="bold">{taxRate}%</Text>
                      </Flex>
                      {taxType === 'short' && (
                        <Slider
                          value={taxRate}
                          onChange={(val) => setTaxRate(val)}
                          min={15}
                          max={40}
                          step={1}
                          colorScheme="orange"
                          mt={2}
                        >
                          <SliderTrack><SliderFilledTrack /></SliderTrack>
                          <SliderThumb />
                        </Slider>
                      )}
                    </Box>
                  )}
                </FormControl>

                {tradePreview && (
                  <Flex justify="center" mt={2}>
                    <Button
                      size="sm"
                      colorScheme="orange"
                      leftIcon={<FontAwesomeIcon icon={faBookmark} />}
                      onClick={savePaperTrade}
                    >
                      Save Position
                    </Button>
                  </Flex>
                )}

              </VStack>

              {/* Right: Preview output */}
              {tradePreview && (() => {
                const sym = currencySymbols[selectedCurrency] || '$';
                const cur = selectedCurrency.toUpperCase();
                const breakevenMovePct = ((tradePreview.breakevenPrice - parsedTradePrice) / parsedTradePrice * 100);
                const isBuy = tradeAction === 'BUY';
                const isDark = colorMode === 'dark';
                const labelColor = isDark ? 'orange.700' : 'blue.500';
                const cardBg = isDark ? 'primary.300' : 'white';
                const cardBgAlt = isDark ? '#1e1e1e' : 'gray.50';
                const borderClr = isDark ? 'whiteAlpha.100' : 'gray.200';
                const subtextColor = isDark ? 'gray.400' : 'gray.500';

                return (
                  <SimpleGrid
                    columns={{ base: 2, md: 1 }}
                    spacing={0}
                    flex={1}
                    borderRadius="md"
                    overflow="hidden"
                    boxShadow={isDark ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 2px rgba(0, 0, 0, 0.24)'}
                    h="fit-content"
                  >
                    {/* Entry */}
                    <Box p={{ base: 2, md: 3 }} bg={cardBg} borderWidth="1px" borderColor={borderClr}>
                      <Text fontSize="11px" fontWeight="700" color={labelColor} mb={2} textTransform="uppercase">Entry</Text>
                      <Flex justify="space-between" align="baseline">
                        <Text fontSize={{ base: 'xs', md: 'sm' }}>
                          {isBuy ? 'Buy' : 'Sell'} {parsedTradeBtc} BTC @ {sym}{formatTradeAmount(parsedTradePrice)}
                        </Text>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="semibold">
                          {sym}{formatTradeAmount(tradePreview.grossAmount)}
                        </Text>
                      </Flex>
                      <Flex justify="space-between" align="baseline" mt={1}>
                        <Text fontSize="xs" color={subtextColor}>
                          {isBuy ? 'Buy' : 'Sell'} fee ({tradeFeeRate}%)
                        </Text>
                        <Text fontSize="xs" color="red.400">
                          -{sym}{formatTradeAmount(tradePreview.fee)}
                        </Text>
                      </Flex>
                      <Flex justify="space-between" align="baseline" mt={1} pt={1} borderTopWidth="1px" borderColor={borderClr}>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="semibold">
                          {isBuy ? 'Total cost' : 'You receive'}
                        </Text>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold" color={isBuy ? undefined : 'green.400'}>
                          {sym}{formatTradeAmount(tradePreview.netAmount)} {cur}
                        </Text>
                      </Flex>
                    </Box>

                    {/* Target */}
                    {tradePreview.targetPrice && (() => {
                      const targetMovePct = ((tradePreview.targetPrice - parsedTradePrice) / parsedTradePrice * 100);
                      const gain = tradePreview.gainAtTarget;
                      const isProfit = gain > 0;
                      const gainColor = isProfit ? 'green.400' : 'red.400';
                      const gainSign = isProfit ? '+' : '';
                      const taxAmount = taxEnabled && isProfit ? gain * (taxRate / 100) : 0;
                      const afterTax = gain - taxAmount;
                      const netGainPct = effectiveTargetPct;
                      const isMove = targetMode === 'move';
                      return (
                        <Box p={{ base: 2, md: 3 }} bg={cardBgAlt} borderWidth="1px" borderColor={borderClr}>
                          <Text fontSize="11px" fontWeight="700" color={gainColor} mb={2} textTransform="uppercase">
                            Target ({isMove ? `${parsedTargetPct}% price move` : `${parsedTargetPct}% net gain`})
                          </Text>
                          <Flex justify="space-between" align="baseline">
                            <Text fontSize={{ base: 'xs', md: 'sm' }}>
                              {isBuy ? 'Sell at' : 'Rebuy at'}
                            </Text>
                            <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="semibold" color={gainColor}>
                              {sym}{formatTradeAmount(tradePreview.targetPrice)}
                            </Text>
                          </Flex>
                          {gain !== null && (() => {
                            const gainBtcAlt = isBuy ? gain / parsedTradePrice : null;
                            const gainFiatAlt = !isBuy ? gain * parsedTradePrice : null;
                            const taxBtcAlt = isBuy && taxAmount ? taxAmount / parsedTradePrice : null;
                            const taxFiatAlt = !isBuy && taxAmount ? taxAmount * parsedTradePrice : null;
                            const afterTaxBtcAlt = isBuy ? afterTax / parsedTradePrice : null;
                            const afterTaxFiatAlt = !isBuy ? afterTax * parsedTradePrice : null;
                            return (
                            <>
                              <Flex justify="space-between" align="baseline" mt={1}>
                                <Text fontSize={{ base: 'xs', md: 'sm' }}>{taxEnabled && isProfit ? 'Profit before tax' : 'Net profit'}</Text>
                                <VStack spacing={0} align="flex-end">
                                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold" color={gainColor}>
                                    {isBuy
                                      ? `${gainSign}${sym}${formatTradeAmount(gain)} ${cur}`
                                      : `${gainSign}${formatBtcValue(gain)} BTC`
                                    }
                                  </Text>
                                  <Text fontSize="xs" color={subtextColor}>
                                    {isBuy
                                      ? `${gainSign}${formatBtcValue(gainBtcAlt)} BTC`
                                      : `${gainSign}${sym}${formatTradeAmount(gainFiatAlt)} ${cur}`
                                    }
                                  </Text>
                                </VStack>
                              </Flex>
                              {taxEnabled && isProfit && taxAmount > 0 && (
                                <>
                                  <Flex justify="space-between" align="baseline" mt={1}>
                                    <Text fontSize="xs" color={subtextColor}>
                                      {taxType === 'long' ? 'Long-term' : 'Short-term'} tax ({taxRate}%)
                                    </Text>
                                    <VStack spacing={0} align="flex-end">
                                      <Text fontSize="xs" color="red.400">
                                        {isBuy
                                          ? `-${sym}${formatTradeAmount(taxAmount)} ${cur}`
                                          : `-${formatBtcValue(taxAmount)} BTC`
                                        }
                                      </Text>
                                      <Text fontSize="xs" color={subtextColor}>
                                        {isBuy
                                          ? `-${formatBtcValue(taxBtcAlt)} BTC`
                                          : `-${sym}${formatTradeAmount(taxFiatAlt)} ${cur}`
                                        }
                                      </Text>
                                    </VStack>
                                  </Flex>
                                  <Flex justify="space-between" align="baseline" mt={1} pt={1} borderTopWidth="1px" borderColor={borderClr}>
                                    <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="semibold">After tax</Text>
                                    <VStack spacing={0} align="flex-end">
                                      <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold" color={afterTax > 0 ? 'green.400' : 'red.400'}>
                                        {isBuy
                                          ? `${afterTax >= 0 ? '+' : ''}${sym}${formatTradeAmount(afterTax)} ${cur}`
                                          : `${afterTax >= 0 ? '+' : ''}${formatBtcValue(afterTax)} BTC`
                                        }
                                      </Text>
                                      <Text fontSize="xs" color={subtextColor}>
                                        {isBuy
                                          ? `${afterTax >= 0 ? '+' : ''}${formatBtcValue(afterTaxBtcAlt)} BTC`
                                          : `${afterTax >= 0 ? '+' : ''}${sym}${formatTradeAmount(afterTaxFiatAlt)} ${cur}`
                                        }
                                      </Text>
                                    </VStack>
                                  </Flex>
                                </>
                              )}
                            </>
                            );
                          })()}
                          <Text fontSize="xs" color={subtextColor} mt={1}>
                            {isMove
                              ? `${parsedTargetPct}% price ${isBuy ? 'rise' : 'drop'} — fees consume ${Math.abs(breakevenMovePct).toFixed(2)}%, net ${netGainPct >= 0 ? '+' : ''}${netGainPct.toFixed(2)}%`
                              : `Requires ${Math.abs(targetMovePct).toFixed(2)}% price ${isBuy ? 'rise' : 'drop'} — fees consume ${Math.abs(breakevenMovePct).toFixed(2)}%, you keep ${parsedTargetPct}%`
                            }
                          </Text>
                        </Box>
                      );
                    })()}

                    {/* Breakeven */}
                    <Box p={{ base: 2, md: 3 }} bg={cardBg} borderWidth="1px" borderColor={borderClr}>
                      <Text fontSize="11px" fontWeight="700" color={labelColor} mb={2} textTransform="uppercase">Breakeven</Text>
                      <Flex justify="space-between" align="baseline">
                        <Text fontSize={{ base: 'xs', md: 'sm' }}>
                          {isBuy ? 'Sell at' : 'Rebuy at'}
                        </Text>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="semibold">
                          {sym}{formatTradeAmount(tradePreview.breakevenPrice)}
                        </Text>
                      </Flex>
                      <Text fontSize="xs" color={subtextColor} mt={1}>
                        Price must {isBuy ? 'rise' : 'drop'} {Math.abs(breakevenMovePct).toFixed(2)}% just to cover the {formatTradeAmount(parsedFeeRate * 200)}% round-trip fee
                      </Text>
                    </Box>

                    {/* Stop Loss */}
                    {tradePreview.stopLossPrice && (
                      <Box p={{ base: 2, md: 3 }} bg={cardBgAlt} borderWidth="1px" borderColor={borderClr}>
                        <Text fontSize="11px" fontWeight="700" color="red.400" mb={2} textTransform="uppercase">Stop Loss (-{parsedStopLossPct}% from entry)</Text>
                        <Flex justify="space-between" align="baseline">
                          <Text fontSize={{ base: 'xs', md: 'sm' }}>
                            {isBuy ? 'Sell at' : 'Rebuy at'}
                          </Text>
                          <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="semibold" color="red.400">
                            {sym}{formatTradeAmount(tradePreview.stopLossPrice)}
                          </Text>
                        </Flex>
                      </Box>
                    )}

                  </SimpleGrid>
                );
              })()}

            </Flex>

            {/* Saved Positions */}
            {paperTrades.length > 0 && (() => {
              const isDark = colorMode === 'dark';
              const labelColor = isDark ? 'orange.700' : 'blue.500';
              return (
                <>
                  <Divider my={4} />
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase">
                      Saved Positions ({paperTrades.length})
                    </Text>
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="orange"
                        leftIcon={<FontAwesomeIcon icon={faFileCsv} />}
                        onClick={exportPositionsCSV}
                      >
                        Export CSV
                      </Button>
                      {paperTrades.length > 1 && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => setPaperTrades([])}
                        >
                          Clear All
                        </Button>
                      )}
                    </HStack>
                  </Flex>
                  <VStack spacing={2} align="stretch" maxH="144px" overflowY="auto">
                    {paperTrades.map((trade) => {
                      const pnl = trade.action === 'BUY'
                        ? (livePriceInCurrency - trade.entryPrice) * trade.btcAmount
                        : (trade.entryPrice - livePriceInCurrency) * trade.btcAmount;
                      const pnlPct = trade.entryPrice * trade.btcAmount
                        ? (pnl / (trade.entryPrice * trade.btcAmount)) * 100
                        : 0;
                      const sym = currencySymbols[trade.currency] || '$';
                      return (
                        <Flex
                          key={trade.id}
                          p={2}
                          borderWidth="1px"
                          borderRadius="md"
                          borderColor={isDark ? 'whiteAlpha.100' : 'gray.200'}
                          bg={isDark ? '#1e1e1e' : 'gray.50'}
                          align="center"
                          justify="space-between"
                          gap={2}
                          flexWrap="wrap"
                          cursor="pointer"
                          onClick={() => loadPaperTrade(trade)}
                          _hover={{ borderColor: 'orange.400', bg: isDark ? '#252525' : 'gray.100' }}
                          transition="all 0.15s"
                        >
                          <HStack spacing={2} minW="0">
                            <Badge colorScheme={trade.action === 'BUY' ? 'green' : 'red'} fontSize="10px">
                              {trade.action}
                            </Badge>
                            <Text fontSize="xs" noOfLines={1}>
                              {formatBtcValue(trade.btcAmount)} BTC @ {sym}{formatTradeAmount(trade.entryPrice)}
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            {trade.targetPrice && (
                              <Text fontSize="xs" color={isDark ? 'gray.400' : 'gray.500'}>
                                T: {sym}{formatTradeAmount(trade.targetPrice)}
                              </Text>
                            )}
                            <Text fontSize="xs" fontWeight="bold" color={pnl >= 0 ? 'green.400' : 'red.400'}>
                              {pnl >= 0 ? '+' : ''}{sym}{formatTradeAmount(pnl)} ({pnl >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%)
                            </Text>
                            <IconButton
                              icon={<FontAwesomeIcon icon={faTrash} />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={(e) => { e.stopPropagation(); deletePaperTrade(trade.id); }}
                              aria-label="Delete position"
                            />
                          </HStack>
                        </Flex>
                      );
                    })}
                  </VStack>
                </>
              );
            })()}

            <Text fontSize="xs" color="gray.500" textAlign="left" mt={4}>
              <Text as="span" fontWeight="bold">Paper Trader is a simulation tool. It is not a trading platform, brokerage, exchange, or financial product.</Text> Saved positions are hypothetical. No orders are placed and no funds move. Nothing here is financial, investment, tax, or legal advice. Displayed targets, breakevens, fees, and projected gains are estimates that ignore slippage, spreads, partial fills, on-chain fees, exchange outages, and real tax treatment. Actual execution will differ, often significantly. Bitcoin is highly volatile and can lose value rapidly. Real risks include exchange insolvency, custodial loss, hacks, lost keys, regulatory changes, frozen accounts, and market manipulation. Past performance does not indicate future results; simulated performance does not represent real trading. Trading Bitcoin is probably a bad idea for most people, and the likelihood of getting rekt on any given Bitcoin trade is very high. You are solely responsible for your decisions. Always consult a qualified professional before acting.
              {' '}
              <Text as="span" display="block" mt={2}>
                If trading feels compulsive or is causing harm, call the National Problem Gambling Helpline at 1-800-MY-RESET (1-800-697-3738), text 800GAM, or chat at <Text as="span" textDecoration="underline">ncpgambling.org</Text>. Help is free, confidential, and available 24/7.
              </Text>
            </Text>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BitcoinCalculatorAdvanced;